import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Chat, useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { useSWRConfig } from "swr";
import { MODELS, MUTATIONS, useMultiModel } from "../../../api";
import { sparkChatPath } from "../../../api/sparkChat";
import type { WorkshopId } from "../../../api/workshop";
import { SparkChatDebug } from "./debug/SparkChatDebug";
import { SparkChatBlockView } from "./SparkChatBlockView";
import type { SparkChatMessage } from "./types";

export const SparkChatWorkshopCtx = createContext<WorkshopId | null>(null);

export type ChatBlockValue = {
  _key: string;
  greeting?: string;
  suggestions?: string[];
  placeholder?: string;
};

const PREVIEW_NOTE = "Der KI-Assistent antwortet nur in einer Klasse.";
const LIMIT_NOTE = "Du hast alle Nachrichten für diesen Chat verbraucht.";

const wordError = (error: Error) => {
  if (error.message.includes("limit reached")) return LIMIT_NOTE;
  if (error.message.includes("Too many")) {
    return "Das waren gerade viele Nachrichten. Warte bitte einen Moment und versuch es dann nochmal.";
  }
  return "Das hat gerade nicht geklappt. Versuch es bitte gleich nochmal.";
};

const chats = new Map<string, Chat<SparkChatMessage>>();

const chatKey = (workshopId: WorkshopId, blockKey: string) =>
  `${workshopId}:${blockKey}`;

const chatFor = (workshopId: WorkshopId, blockKey: string) => {
  const key = chatKey(workshopId, blockKey);
  const existing = chats.get(key);
  if (existing) return existing;
  const chat = new Chat<SparkChatMessage>({
    id: crypto.randomUUID(),
    transport: new DefaultChatTransport<SparkChatMessage>({
      api: `${import.meta.env.VITE_API_HOST}${sparkChatPath(workshopId, blockKey)}`,
      credentials: "include",
    }),
  });
  chats.set(key, chat);
  return chat;
};

type LiveChatBlockProps = {
  workshopId: WorkshopId;
  block: ChatBlockValue;
  greeting: string;
  chat: Chat<SparkChatMessage>;
  onNewChat: () => void;
};

const LiveChatBlock = ({
  workshopId,
  block,
  greeting,
  chat,
  onNewChat,
}: LiveChatBlockProps) => {
  const infoUrl = MODELS.SPARK_CHAT_INFO(workshopId, block._key);
  const [info] = useMultiModel([infoUrl], { lazy: true });
  const { mutate } = useSWRConfig();
  const refreshInfo = () => void mutate(String(infoUrl));
  const chatId = chat.id;

  const { messages, sendMessage, status, error, clearError } =
    useChat<SparkChatMessage>({ chat });

  const previousStatus = useRef(status);
  useEffect(() => {
    const finished =
      previousStatus.current !== status &&
      (status === "ready" || status === "error");
    previousStatus.current = status;
    if (finished) refreshInfo();
  });

  if (info && !info.enabled) return null;

  const busy = status === "submitted" || status === "streaming";
  const limitReached = info?.enabled && info.messagesLeft <= 0 && !busy;

  const send = (text: string) => {
    if (error) clearError();
    void sendMessage({ text });
  };

  return (
    <>
      <SparkChatBlockView
        greeting={greeting}
        suggestions={block.suggestions}
        placeholder={block.placeholder}
        maxMessageLength={info?.enabled ? info.maxMessageLength : 0}
        messages={messages}
        status={status}
        errorMessage={error && !limitReached ? wordError(error) : undefined}
        disabled={!info}
        note={limitReached ? LIMIT_NOTE : undefined}
        onSend={send}
      />
      {info?.enabled && info.debug && (
        <SparkChatDebug
          sparkTitle={`KI-Chat ${block._key}`}
          chatId={chatId}
          messages={messages}
          busy={busy}
          onReset={() =>
            MUTATIONS.resetSparkChat({
              workshopId,
              blockKey: block._key,
              chatId,
            }).then(() => {
              refreshInfo();
              onNewChat();
            })
          }
        />
      )}
    </>
  );
};

const LiveChatBlockHost = ({
  workshopId,
  block,
  greeting,
}: Omit<LiveChatBlockProps, "chat" | "onNewChat">) => {
  const [chat, setChat] = useState(() => chatFor(workshopId, block._key));
  return (
    <LiveChatBlock
      key={chat.id}
      workshopId={workshopId}
      block={block}
      greeting={greeting}
      chat={chat}
      onNewChat={() => {
        chats.delete(chatKey(workshopId, block._key));
        setChat(chatFor(workshopId, block._key));
      }}
    />
  );
};

export const SparkChatBlock = ({ value }: { value: ChatBlockValue }) => {
  const workshopId = useContext(SparkChatWorkshopCtx);
  const greeting = value.greeting?.trim();

  if (!greeting) return null;

  if (!workshopId) {
    return (
      <SparkChatBlockView
        greeting={greeting}
        suggestions={value.suggestions}
        maxMessageLength={0}
        messages={[]}
        status="ready"
        disabled
        note={PREVIEW_NOTE}
        onSend={() => {}}
      />
    );
  }

  return (
    <LiveChatBlockHost
      workshopId={workshopId}
      block={value}
      greeting={greeting}
    />
  );
};
